// Old study-patients lists that belong to a study which was later re-created.
//
// `studies_copy/{id}` holds each study's enrolled list (`patients`). Some
// studies were deleted and re-created in `studies` with a new doc id, while
// their list stayed under the OLD id. Those old copy docs have no matching
// `studies` doc ("orphans"), so:
//   - the backfill named their matches by the raw id, and the move rule and
//     study close couldn't see the real study;
//   - the D-6 copy repair then created a second, empty copy doc under the NEW
//     id, so the study appears twice on the study-patients page.
//
// The merge (functions/mergeOrphanStudyCopies.mjs) pairs each orphan with its
// current study, copies the orphan's rows into the current copy doc, re-points
// patients' enrollments at the current study, and marks the orphan
// `MERGED_INTO: <current id>`. Nothing is deleted; every reader of studies_copy
// hides merged docs with isMergedStudyCopy.
//
// Import-free and Firebase-free so Jest and the plain-Node script share it.

export const isMergedStudyCopy = (copyDoc) => !!(copyDoc && copyDoc.MERGED_INTO);

const key = (v) => String(v ?? "").trim().toLowerCase();

/**
 * Pair orphan copy docs with the current study they belong to.
 * A match is on STUDY_ID, else NAME (trimmed, case-insensitive). Exactly one
 * candidate is required; two or more is reported as ambiguous and skipped.
 * An orphan already marked MERGED_INTO keeps that pairing.
 *
 * @param {Array<object>} copies - studies_copy docs ({id, STUDY_ID, NAME, MERGED_INTO?})
 * @param {Array<object>} studies - studies docs ({id, STUDY_ID, NAME, ...})
 * @returns {{pairs: Array<{orphan, study, basis}>, ambiguous: Array<{orphan, candidates}>,
 *            unmatched: Array<object>}}
 */
export const pairOrphanCopies = (copies, studies) => {
  const studyIds = new Set((studies || []).map((s) => s.id));
  const byId = new Map((studies || []).map((s) => [s.id, s]));
  const pairs = [];
  const ambiguous = [];
  const unmatched = [];

  for (const orphan of copies || []) {
    if (!orphan || studyIds.has(orphan.id)) continue; // has its own study
    if (orphan.MERGED_INTO && byId.has(orphan.MERGED_INTO)) {
      pairs.push({ orphan, study: byId.get(orphan.MERGED_INTO), basis: "already merged" });
      continue;
    }
    const sid = key(orphan.STUDY_ID);
    const name = key(orphan.NAME);
    let candidates = sid ? studies.filter((s) => key(s.STUDY_ID) === sid) : [];
    let basis = "STUDY_ID";
    if (!candidates.length && name) {
      candidates = studies.filter((s) => key(s.NAME) === name);
      basis = "NAME";
    }
    if (candidates.length === 1) pairs.push({ orphan, study: candidates[0], basis });
    else if (candidates.length > 1) ambiguous.push({ orphan, candidates });
    else unmatched.push(orphan);
  }
  return { pairs, ambiguous, unmatched };
};

/**
 * Re-point a patient's "Potential Studies" ticks (`potentialStudyIds`) from
 * orphan copy ids to the current study, keeping order and never adding a
 * duplicate tick. Other ids are untouched.
 * @returns {{ids: string[], changed: Array<{from: string, to: string}>}}
 */
export const relinkPotentialIds = (ids, studyForOrphan) => {
  const out = [];
  const changed = [];
  for (const id of Array.isArray(ids) ? ids : []) {
    const study = studyForOrphan.get(String(id));
    const next = study ? study.id : id;
    if (study) changed.push({ from: id, to: study.id });
    if (!out.includes(next)) out.push(next);
  }
  return { ids: out, changed };
};

/**
 * Re-point a patient's enrollment entries from orphan copy ids to the current
 * study. Returns a NEW list and how many entries changed; entries for other
 * studies are untouched. The old id is kept on the entry as `relinkedFrom`.
 * @param {Array<object>} entries
 * @param {Map<string, object>} studyForOrphan - orphan id → current study doc
 */
export const relinkEntries = (entries, studyForOrphan, { at } = {}) => {
  let changed = 0;
  const list = (Array.isArray(entries) ? entries : []).map((e) => {
    const study = e && studyForOrphan.get(String(e.studyId));
    if (!study) return e;
    changed += 1;
    return {
      ...e,
      studyId: study.id,
      studyName: study.NAME || study.STUDY_ID || study.id,
      disease: study.DISEASE || e.disease || "",
      relinkedFrom: e.studyId,
      relinkedAt: at || null,
    };
  });
  return { list, changed };
};
